'use client';
import { useEffect } from 'react';

import { useApi } from '@/hooks/useApi';

import { BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { BlockComponentModel } from '@/cms/types/blok-component';
import Heading from '@/components/atoms/Heading';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';
import { Spinner } from '@/components/atoms/spinner';
import { Text } from '@/components/atoms/Text';
import { HomepageLinkButtonStrapi } from '@/features/HomepageHero/components/HomepageLinkButton';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { getIntlText } from '@/lib/i18n/i18n.server';
import { cn } from '@/lib/utils';
import iconDeviceSrc from '~/svg/IconDevice.svg';
import iconMapMarkerSrc from '~/svg/IconMapMarker.svg';
import iconPhoneCallSrc from '~/svg/IconPhoneCall.svg';
import iconUniversalSrc from '~/svg/IconUniversal.svg';
import { SvgIcon } from '@/components/SvgIcon';

export type WhatIsMyIpHeroProps = {
  buttons?: LinkButtonStrapiProps[];
  image: ImageStrapiProps;
};

type IpData = {
  browser: { name: string; version: string };
  platform: { os: string; name?: string };
  engine: unknown;
  isp: {
    isp: string;
    asn: number;
    domain: string;
    organization: string;
  };
  city: {
    name: string;
    code: string;
    state: string;
    time_zone: string;
    zip_code: string;
    latitude: number;
    longitude: number;
  };
  proxy: { ip: string };
  country: { code: string; name: string; continent: string };
};

export function WhatIsMyIpHeroStrapi({
  block,
  ...rest
}: BlockComponentModel<WhatIsMyIpHeroProps>) {
  const { texts } = useTranslation();

  const { buttons, image } = block;
  const { data, makeApiCall } = useApi<IpData>({
    url: `/next-api/my-ip`,
  });

  useEffect(() => {
    makeApiCall();
  }, []);

  return (
    <>
      <BlockWrapper
        {...rest}
        contentFit='contained'
        data-tracker-section={SECTION_NAMES.WHAT_IS_MY_IP}
      >
        <div>
          <BlockHeaderContent alignment='left'>
            <div className='flex w-full flex-row justify-between gap-16'>
              <div className='flex w-full max-w-[600px] flex-col justify-center gap-6'>
                <Heading level='h1'>
                  {getIntlText(
                    texts.what_is_my_ip_hero.your_public_ip_address,
                    {
                      ip: data?.proxy.ip || '',
                    }
                  )}
                </Heading>
                <Text as='div' classNames={{ wrapper: 'flex' }}>
                  {texts.what_is_my_ip_hero.location}:{' '}
                  {data ? data?.city.name : <Spinner className='ml-4' />}
                </Text>
                <Text as='div' classNames={{ wrapper: 'flex' }}>
                  {getIntlText(texts.what_is_my_ip_hero.country)}:{' '}
                  {data ? data?.country.code : <Spinner className='ml-4' />}
                </Text>
                <div className='flex gap-6 pt-2'>
                  {buttons?.map((button, index) => (
                    <HomepageLinkButtonStrapi
                      key={index}
                      block={button}
                      eventName='hero_cta_click'
                      inner={{ classNames: { root: 'w-full md:w-auto' } }}
                    />
                  ))}
                </div>
              </div>
              <div className={cn('hidden', 'md:px-15 md:block')}>
                <ImageStrapi
                  block={image}
                  inner={{
                    classNames: {
                      image: 'md:max-w-[460px] md:max-h-[460px]',
                    },
                  }}
                />
              </div>
            </div>
          </BlockHeaderContent>
        </div>
      </BlockWrapper>
      <BlockWrapper
        data-tracker-section={SECTION_NAMES.WHAT_IS_MY_IP}
        {...rest}
      >
        <div>
          <BlockHeaderContent alignment='left'>
            <div className='flex w-full flex-wrap justify-between gap-6'>
              <div className='flex w-[200px]  flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <SvgIcon
                    src={iconMapMarkerSrc}
                    className='text-brand-500 h-9 w-9'
                  />
                  <Text variant='subtitle-l-d'>
                    {getIntlText(texts.what_is_my_ip_hero.location)}
                  </Text>
                </div>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.city)}:{' '}
                  {data?.city.name}
                </Text>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.code)}:{' '}
                  {data?.city.code}
                </Text>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.state)}:{' '}
                  {data?.city.state}
                </Text>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.time_zone)}:{' '}
                  {data?.city.time_zone}
                </Text>
              </div>
              <div className='flex w-[200px]  flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <SvgIcon
                    src={iconPhoneCallSrc}
                    className='text-brand-500 h-9 w-9'
                  />
                  <Text variant='subtitle-l-d'>
                    {getIntlText(texts.what_is_my_ip_hero.isp)}
                  </Text>
                </div>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.provider)}:{' '}
                  {data?.isp.isp}
                </Text>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.asn)}: {data?.isp.asn}
                </Text>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.organization)}:{' '}
                  {data?.isp.organization}
                </Text>
              </div>
              <div className='flex w-[200px]  flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <SvgIcon
                    src={iconDeviceSrc}
                    className='text-brand-500 h-9 w-9'
                  />
                  <Text variant='subtitle-l-d'>
                    {getIntlText(texts.what_is_my_ip_hero.platform)}
                  </Text>
                </div>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.name)}:{' '}
                  {data?.platform?.name}
                </Text>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.os)}:{' '}
                  {data?.platform?.os}
                </Text>
              </div>
              <div className='flex w-[200px]  flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <SvgIcon
                    src={iconUniversalSrc}
                    className='text-brand-500 h-9 w-9'
                  />
                  <Text variant='subtitle-l-d'>
                    {getIntlText(texts.what_is_my_ip_hero.browser)}
                  </Text>
                </div>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.name)}:{' '}
                  {data?.browser.name}
                </Text>
                <Text>
                  {getIntlText(texts.what_is_my_ip_hero.version)}:{' '}
                  {data?.browser.version}
                </Text>
              </div>
            </div>
          </BlockHeaderContent>
        </div>
      </BlockWrapper>
    </>
  );
}
